/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { DeviceType, useDevice } from '@squiz/dxp-console-support';
import clsx from 'clsx';
import React from 'react';
import { DXP_DATA_ID_TOOLBAR_BUTTON } from '../../Constants/DataIds';
import { Dropdown } from '../Dropdown';
import { SdsIcon } from '../SdsIcon';
import { SideNavLauncher } from '../SideNav/SideNavLauncher';
import { ToolbarProps, toolbarDefaults } from './Toolbar.props';

/**
 * SDS Toolbar component.
 */
export const Toolbar = (props: ToolbarProps): JSX.Element => {
  const {
    classes,
    clientName,
    logo,
    menuSections,
    showTabletNavigation,
    switcher,
    text,
    toolbarContent,
    toolbarMenus,
    toolbarUser,
    ...rest
  } = {
    ...toolbarDefaults,
    ...props,
  };

  // Device detection
  const deviceType = useDevice();
  const isMobile = deviceType === DeviceType.mobile;
  const isTablet = deviceType === DeviceType.tablet;
  const isTabletSmall = deviceType === DeviceType.tabletSmall;

  // SideNav setup
  const sideNav = (
    <SideNavLauncher data-id={DXP_DATA_ID_TOOLBAR_BUTTON} device={deviceType} menuSections={menuSections} />
  );

  // Toolbar menus setup
  const getToolbarMenus = (): Array<JSX.Element> => {
    return toolbarMenus.map((itemProps): JSX.Element => {
      return (
        <li className="sds-toolbar-menus__item" key={itemProps.key}>
          <Dropdown {...itemProps} />
        </li>
      );
    });
  };

  const toolbarClass = clsx(`sds-toolbar`, classes);
  return (
    <>
      <div className={toolbarClass} {...rest}>
        {(isTablet || isTabletSmall) && showTabletNavigation && <>{sideNav}</>}
        <div className="sds-toolbar-branding">
          <div className="sds-toolbar-logo">
            {switcher ?? <SdsIcon classes={`sds-toolbar-logo--plain`} svg={logo} />}
          </div>
          {!isMobile && <span className="sds-toolbar-branding__text">{text}</span>}
          <div className="sds-toolbar-client">
            {typeof clientName === 'string' ? <span>{clientName}</span> : clientName}
          </div>
        </div>
        <div className="sds-toolbar-content">{toolbarContent}</div>
        {isMobile && <>{sideNav}</>}
        <div className="sds-toolbar-tools">
          {toolbarMenus.length > 0 && (
            <div className="sds-toolbar-menus">
              <ul className="sds-toolbar-menus__list">{getToolbarMenus()}</ul>
            </div>
          )}
          {toolbarUser && (
            <div className="sds-toolbar-user__wrapper">
              <div className="sds-toolbar-user">{toolbarUser}</div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
